/* ============================================================
   PintaFlow — Design Direction V1: "Electric Operator"
   ----------------------------------------------------------
   - Color: Black + White + Electric Blue (#2563EB → #3B82F6)
   - Feel: Linear × engineering blueprint × terminal
   - Type: Space Grotesk (display) + JetBrains Mono (eyebrows)
   - Hero: Pipeline IS the hero, rendered as a wired diagram
   ============================================================ */
import { ArrowUpRight, Terminal } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const STAGES = [
  { id: 1, label: "IDEA", note: "Capture every spark." },
  { id: 2, label: "RESEARCH", note: "Gather context." },
  { id: 3, label: "SCRIPT", note: "Write the hook." },
  { id: 4, label: "CREATE", note: "Record. Edit. Ship." },
  { id: 5, label: "SCHEDULE", note: "Pick the moment." },
  { id: 6, label: "PUBLISHED", note: "Out in the wild." },
];

export default function LandingV1() {
  return (
    <div className="min-h-screen bg-[#070809] text-white overflow-x-hidden" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* Subtle blueprint grid */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #2563EB 1px, transparent 1px), linear-gradient(to bottom, #2563EB 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      {/* Top bar */}
      <header className="relative border-b border-white/5 backdrop-blur-sm">
        <div className="max-w-[1320px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-md bg-[#2563EB] flex items-center justify-center shadow-[0_0_24px_rgba(37,99,235,0.55)]">
              <div className="w-2.5 h-2.5 bg-white rounded-sm" />
            </div>
            <span className="text-lg font-semibold tracking-tight">PintaFlow</span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-white/40 font-mono ml-3 hidden sm:inline">/ creator OS</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden md:inline text-[10px] font-mono text-white/40 uppercase tracking-[0.2em]">v1.0 — direction electric</span>
            <button onClick={startGoogleLogin} className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1.5 transition">
              Sign in <ArrowUpRight size={13} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero — Pipeline as protagonist */}
      <section className="relative max-w-[1320px] mx-auto px-6 pt-16 md:pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Left rail: tagline */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[#2563EB]/40 bg-[#2563EB]/10 text-[#7FA8FF] text-[10px] uppercase tracking-[0.22em] font-mono mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] animate-pulse" />
              now in private beta
            </div>
            <h1 className="text-[44px] sm:text-[64px] leading-[0.95] font-medium tracking-tight">
              The operating <br />system for <br />
              <span className="text-[#3B82F6]">serious creators.</span>
            </h1>
            <p className="mt-6 text-white/55 text-[15px] leading-relaxed max-w-md">
              PintaFlow runs your craft like a studio. Capture, research, write, ship — every idea routed
              through a pipeline that respects your focus.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={startGoogleLogin}
                className="group inline-flex items-center gap-2 bg-white text-black px-5 py-3 rounded-md font-medium text-sm hover:bg-[#3B82F6] hover:text-white transition-colors"
              >
                Boot the OS <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
              </button>
              <span className="text-[11px] font-mono text-white/40 uppercase tracking-[0.22em]">
                G · sign in with google
              </span>
            </div>
            <div className="mt-10 flex items-center gap-6 text-[11px] font-mono text-white/35 uppercase tracking-[0.18em]">
              <span>5 platforms</span><span>·</span><span>6 stages</span><span>·</span><span>1 brain</span>
            </div>
          </div>

          {/* Right: Pipeline blueprint */}
          <div className="lg:col-span-7 relative">
            <div className="absolute -top-3 left-0 right-0 flex items-center justify-between text-[10px] font-mono text-white/30 uppercase tracking-[0.22em]">
              <span className="flex items-center gap-1.5"><Terminal size={10} /> pipeline.flow</span>
              <span>idea → published</span>
            </div>
            <div className="border border-white/10 rounded-xl bg-gradient-to-br from-white/[0.02] to-transparent p-5 md:p-8">
              {/* Stages */}
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
                {STAGES.map((s, i) => (
                  <div key={s.id} className="relative">
                    <div className="rounded-lg border border-white/10 bg-black/40 px-3 py-3 hover:border-[#3B82F6]/60 hover:bg-[#2563EB]/[0.08] transition group">
                      <div className="text-[9px] font-mono text-[#3B82F6] tracking-[0.2em]">0{s.id}</div>
                      <div className="text-[11px] font-semibold tracking-tight mt-1.5">{s.label}</div>
                      <div className="text-[10px] text-white/40 mt-1 leading-snug">{s.note}</div>
                    </div>
                    {i < STAGES.length - 1 && (
                      <div className="hidden lg:flex absolute top-1/2 -right-2 w-3 h-px bg-gradient-to-r from-[#3B82F6] to-transparent" />
                    )}
                  </div>
                ))}
              </div>

              {/* Sample cards moving through */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { title: "Why I quit FAANG (3min hook test)", platform: "YT LONG", stage: "SCRIPT", color: "#F59E0B" },
                  { title: "Editing for retention — under 60s", platform: "REELS", stage: "CREATE", color: "#3B82F6" },
                  { title: "Tools I used to grow to 100k", platform: "TWEET", stage: "PUBLISHED", color: "#10B981" },
                  { title: "How to research a niche fast", platform: "BLOG", stage: "RESEARCH", color: "#3B82F6" },
                ].map((c, i) => (
                  <div key={i} className="rounded-lg border border-white/10 bg-black/50 p-3 hover:border-white/20 transition group">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[9px] font-mono px-1.5 py-0.5 bg-white/5 rounded text-white/60 tracking-wider">{c.platform}</span>
                      <span className="text-[9px] font-mono tracking-wider" style={{ color: c.color }}>● {c.stage}</span>
                    </div>
                    <div className="text-[13px] text-white/85 leading-snug">{c.title}</div>
                  </div>
                ))}
              </div>

              {/* Bottom telemetry */}
              <div className="mt-8 pt-5 border-t border-white/5 grid grid-cols-3 gap-3 text-center">
                {[
                  ["12", "ideas captured"],
                  ["07", "in pipeline"],
                  ["04", "published this week"],
                ].map(([n, l], i) => (
                  <div key={i}>
                    <div className="text-2xl font-medium" style={{ color: i === 2 ? "#3B82F6" : "white" }}>{n}</div>
                    <div className="text-[9px] font-mono text-white/40 uppercase tracking-[0.2em] mt-1">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto strip */}
      <section className="relative border-y border-white/5 py-12 md:py-16 bg-black/40">
        <div className="max-w-[1320px] mx-auto px-6">
          <div className="text-[10px] font-mono text-white/40 uppercase tracking-[0.28em] mb-5">// what it is</div>
          <h2 className="text-2xl sm:text-4xl font-medium leading-tight tracking-tight max-w-3xl">
            Not a notes app. Not an AI generator. <span className="text-white/40">PintaFlow is the operating layer between your ideas and the audience that&apos;s waiting.</span>
          </h2>
        </div>
      </section>

      {/* Capability cards */}
      <section className="max-w-[1320px] mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            ["IDEA VAULT", "Tags, filters, search. Nothing leaks."],
            ["SWIPE FILE", "URLs auto-fetch their cover. Inspiration, indexed."],
            ["STREAK", "A monochrome heatmap. No gamification confetti."],
            ["GOALS", "Weekly + monthly. Auto-progress on publish."],
            ["CALENDAR", "Your publishing month, at a glance."],
            ["OFFLINE PWA", "Capture without signal. Sync when you land."],
          ].map(([t, d], i) => (
            <div key={t} className="border border-white/10 rounded-lg p-5 hover:border-[#3B82F6]/40 hover:bg-[#2563EB]/[0.04] transition group">
              <div className="text-[10px] font-mono text-[#3B82F6] tracking-[0.22em]">SYS.{String(i + 1).padStart(2, "0")}</div>
              <div className="text-base font-medium mt-3">{t}</div>
              <div className="text-sm text-white/50 mt-1.5 leading-relaxed">{d}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-[1320px] mx-auto px-6 flex flex-wrap justify-between text-[10px] font-mono text-white/30 uppercase tracking-[0.22em]">
          <span>© 2026 pintaflow</span>
          <span>direction · electric operator · v1</span>
        </div>
      </footer>
    </div>
  );
}
